/**
 * DataLayer Event Tracking Utilities
 * Provides automatic event tracking for clicks, scrolls, videos, forms, and downloads.
 */

// Store event listeners for cleanup
let eventListeners = [];
let scrollThresholds = { 25: false, 50: false, 75: false, 100: false };
let intersectionObserver = null;

/**
 * Push event to dataLayer
 * @param {string} eventName - Event name
 * @param {Object} data - Event data
 */
const pushToDataLayer = (eventName, data = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...data,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track button clicks
 * Listens for clicks on buttons and elements with data-track-click attribute
 */
const initClickTracking = () => {
  const handleClick = (event) => {
    const target = event.target.closest('button, [data-track-click], a[data-track-button]');
    
    if (target) {
      const trackData = {
        button: {
          id: target.id || target.getAttribute('data-track-id') || 'unknown',
          text: target.innerText?.substring(0, 100) || '',
          classes: target.className || '',
          page: window.location.pathname
        }
      };
      
      // Add custom tracking attributes
      if (target.getAttribute('data-track-category')) {
        trackData.button.category = target.getAttribute('data-track-category');
      }
      if (target.getAttribute('data-track-action')) {
        trackData.button.action = target.getAttribute('data-track-action');
      }
      if (target.getAttribute('data-track-label')) {
        trackData.button.label = target.getAttribute('data-track-label');
      }
      
      pushToDataLayer('button_click', trackData);
    }
  };

  document.addEventListener('click', handleClick, true);
  eventListeners.push({ type: 'click', handler: handleClick, element: document });
};

/**
 * Track link clicks
 * Tracks outbound links and links with data-track-link attribute
 */
const initLinkTracking = () => {
  const handleLinkClick = (event) => {
    const link = event.target.closest('a[href]');
    
    if (link) {
      const href = link.getAttribute('href');
      const isExternal = href && (href.startsWith('http') && !href.includes(window.location.hostname));
      const isTracked = link.hasAttribute('data-track-link');
      
      if (isExternal || isTracked) {
        pushToDataLayer('link_click', {
          link: {
            url: href,
            text: link.innerText?.substring(0, 100) || '',
            isExternal,
            page: window.location.pathname
          }
        });
      }
    }
  };

  document.addEventListener('click', handleLinkClick, true);
  eventListeners.push({ type: 'click', handler: handleLinkClick, element: document });
};

/**
 * Track scroll depth at 25%, 50%, 75%, and 100%
 */
const initScrollTracking = () => {
  const handleScroll = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollPercent = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);

    const thresholds = [25, 50, 75, 100];
    
    thresholds.forEach((threshold) => {
      if (scrollPercent >= threshold && !scrollThresholds[threshold]) {
        scrollThresholds[threshold] = true;
        pushToDataLayer('scroll_depth', {
          scroll: {
            depth: threshold,
            page: window.location.pathname,
            title: document.title
          }
        });
      }
    });
  };

  // Throttle scroll events
  let scrollTimeout;
  const throttledScroll = () => {
    if (scrollTimeout) return;
    scrollTimeout = setTimeout(() => {
      handleScroll();
      scrollTimeout = null;
    }, 100);
  };

  window.addEventListener('scroll', throttledScroll, { passive: true });
  eventListeners.push({ type: 'scroll', handler: throttledScroll, element: window });
};

/**
 * Track video plays using Intersection Observer and native video events
 */
const initVideoTracking = () => {
  // Track HTML5 video elements
  const trackVideo = (video) => {
    const videoId = video.id || video.getAttribute('data-video-id') || video.src || 'unknown';
    const videoTitle = video.getAttribute('data-video-title') || video.getAttribute('title') || videoId;

    const handlePlay = () => {
      pushToDataLayer('video_play', {
        video: {
          id: videoId,
          title: videoTitle,
          currentTime: Math.round(video.currentTime),
          duration: Math.round(video.duration),
          page: window.location.pathname
        }
      });
    };

    const handlePause = () => {
      pushToDataLayer('video_pause', {
        video: {
          id: videoId,
          title: videoTitle,
          currentTime: Math.round(video.currentTime),
          percentComplete: Math.round((video.currentTime / video.duration) * 100)
        }
      });
    };

    const handleEnded = () => {
      pushToDataLayer('video_complete', {
        video: {
          id: videoId,
          title: videoTitle,
          duration: Math.round(video.duration)
        }
      });
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    eventListeners.push({ type: 'play', handler: handlePlay, element: video });
    eventListeners.push({ type: 'pause', handler: handlePause, element: video });
    eventListeners.push({ type: 'ended', handler: handleEnded, element: video });
  };

  // Track existing videos
  document.querySelectorAll('video').forEach(trackVideo);

  // Watch for dynamically added videos
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeName === 'VIDEO') {
          trackVideo(node);
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('video').forEach(trackVideo);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Track YouTube iframes
  const trackYouTubeIframes = () => {
    document.querySelectorAll('iframe[src*="youtube.com"], iframe[src*="youtu.be"]').forEach((iframe) => {
      if (!iframe.hasAttribute('data-youtube-tracked')) {
        iframe.setAttribute('data-youtube-tracked', 'true');
        pushToDataLayer('video_impression', {
          video: {
            id: iframe.src,
            type: 'youtube',
            page: window.location.pathname
          }
        });
      }
    });
  };

  trackYouTubeIframes();

  // Store reference for cleanup
  intersectionObserver = observer;
};

/**
 * Track file downloads based on file extensions
 */
const initFileDownloadTracking = () => {
  const fileExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip', 'rar', 'csv', 'txt', 'mp3', 'mp4', 'avi', 'mov', 'wmv'];

  const handleDownloadClick = (event) => {
    const link = event.target.closest('a[href]');
    
    if (link) {
      const href = link.getAttribute('href');
      const extension = href?.split('.').pop()?.toLowerCase()?.split('?')[0];
      
      if (extension && fileExtensions.includes(extension)) {
        const fileName = href.split('/').pop()?.split('?')[0] || 'unknown';
        
        pushToDataLayer('file_download', {
          file: {
            name: fileName,
            type: extension,
            url: href,
            page: window.location.pathname
          }
        });
      }
    }
  };

  document.addEventListener('click', handleDownloadClick, true);
  eventListeners.push({ type: 'click', handler: handleDownloadClick, element: document });
};

/**
 * Track form submissions
 */
const initFormTracking = () => {
  const handleSubmit = (event) => {
    const form = event.target;
    
    if (form.tagName === 'FORM') {
      const formId = form.id || form.getAttribute('data-form-id') || form.getAttribute('name') || 'unknown';
      const formName = form.getAttribute('data-form-name') || form.getAttribute('name') || formId;
      
      // Count form fields (excluding hidden and submit)
      const visibleFields = form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]), select, textarea');
      
      pushToDataLayer('form_submission', {
        form: {
          id: formId,
          name: formName,
          action: form.action || window.location.href,
          method: form.method || 'GET',
          fieldCount: visibleFields.length,
          page: window.location.pathname
        }
      });
    }
  };

  document.addEventListener('submit', handleSubmit, true);
  eventListeners.push({ type: 'submit', handler: handleSubmit, element: document });

  // Also track form field interactions for engagement analysis
  const handleFormFocus = (event) => {
    const input = event.target;
    const form = input.closest('form');
    
    if (form && (input.tagName === 'INPUT' || input.tagName === 'SELECT' || input.tagName === 'TEXTAREA')) {
      pushToDataLayer('form_field_focus', {
        form: {
          id: form.id || form.getAttribute('name') || 'unknown',
          field: {
            name: input.name || input.id || 'unknown',
            type: input.type || input.tagName.toLowerCase()
          }
        }
      });
    }
  };

  document.addEventListener('focusin', handleFormFocus, true);
  eventListeners.push({ type: 'focusin', handler: handleFormFocus, element: document });
};

/**
 * Initialize all dataLayer event tracking
 * @param {Object} options - Configuration options
 */
export const initDataLayerEvents = (options = {}) => {
  const {
    trackClicks = true,
    trackScrollDepth = true,
    trackVideoPlays = true,
    trackFileDownloads = true,
    trackFormSubmissions = true
  } = options;

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];

  if (trackClicks) {
    initClickTracking();
    initLinkTracking();
  }

  if (trackScrollDepth) {
    initScrollTracking();
  }

  if (trackVideoPlays) {
    initVideoTracking();
  }

  if (trackFileDownloads) {
    initFileDownloadTracking();
  }

  if (trackFormSubmissions) {
    initFormTracking();
  }

  console.log('DataLayer events initialized', options);
};

/**
 * Cleanup all event listeners
 */
export const cleanupDataLayerEvents = () => {
  eventListeners.forEach(({ type, handler, element }) => {
    element.removeEventListener(type, handler, true);
  });
  eventListeners = [];

  // Reset scroll thresholds
  scrollThresholds = { 25: false, 50: false, 75: false, 100: false };

  // Disconnect intersection observer
  if (intersectionObserver) {
    intersectionObserver.disconnect();
    intersectionObserver = null;
  }

  console.log('DataLayer events cleaned up');
};

/**
 * Manually push custom event to dataLayer
 * @param {string} eventName - Event name
 * @param {Object} data - Event data
 */
export const pushDataLayerEvent = pushToDataLayer;

export default {
  initDataLayerEvents,
  cleanupDataLayerEvents,
  pushDataLayerEvent
};
